import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form'

import { getByPath } from './getByPath'

/**
 * オブジェクトから指定されたパスの値が等しいかを判定します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param obj - オブジェクト。
 * @param path - パス。
 * @param value - 値。
 * @returns 指定されたパスの値が等しい場合は true 、そうでない場合は false 。
 */
export function equalsByPath<T extends FieldValues, P extends FieldPath<T>>(
  obj: T,
  path: P,
  value: FieldPathValue<T, P>,
): boolean

/**
 * オブジェクトから指定されたパスの値が等しいかを判定します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param path - パス。
 * @param value - 値。
 * @returns オブジェクトを引数に取り、指定されたパスの値が等しいかを判定する関数。
 */
export function equalsByPath<T extends FieldValues, P extends FieldPath<T>>(
  path: P,
  value: FieldPathValue<T, P>,
): (obj: T) => boolean

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function equalsByPath<T extends FieldValues, P extends FieldPath<T>>(
  ...args: [P, FieldPathValue<T, P>] | [T, P, FieldPathValue<T, P>]
): boolean | ((obj: T) => boolean) {
  const equalsByPathInner = (obj: T, path: P, value: FieldPathValue<T, P>) =>
    getByPath(obj, path) === value

  if (args.length === 2) {
    return (obj: T) => equalsByPathInner(obj, ...args)
  }

  return equalsByPathInner(...args)
}
